import { useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { TeamBillingPriceDTO } from "@shaarp/common";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

type PricesContextType = {
  prices: TeamBillingPriceDTO[];
  apiFetchPrices: () => Promise<void>;
  apiFetchLoading: boolean;
};

const PricesContext = createContext<PricesContextType>({
  prices: [],
  apiFetchPrices: async () => {},
  apiFetchLoading: false,
});

export const usePrices = (): PricesContextType => {
  const context = useContext(PricesContext);
  if (!context) {
    throw new Error("usePrices must be used within an PricesProvider");
  }
  return context;
};

export const PricesProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const toastError = useToastError();
  const api = useApi();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [prices, setPrices] = useState<TeamBillingPriceDTO[]>([]);

  // Persist the in-flight promise across renders so concurrent callers share it
  const fetchPricesPromiseRef = useRef<Promise<void> | null>(null);

  const fetchPrices = useCallback(
    async ({ force = false }: { force?: boolean } = {}) => {
      if ((!force && prices.length) || isLoading) {
        return;
      }

      if (fetchPricesPromiseRef.current) {
        await fetchPricesPromiseRef.current;
        return;
      }

      fetchPricesPromiseRef.current = (async () => {
        try {
          setIsLoading(true);
          const { data } = await api.get<TeamBillingPriceDTO[]>(
            `/my/team/billing/prices`,
          );
          setPrices(data);
        } catch (error) {
          toastError();
        } finally {
          setIsLoading(false);
          fetchPricesPromiseRef.current = null;
        }
      })();

      await fetchPricesPromiseRef.current;
    },
    [api, isLoading, toastError, prices.length],
  );

  const value = useMemo(
    () => ({
      prices,
      apiFetchPrices: fetchPrices,
      apiFetchLoading: isLoading,
    }),
    [prices, fetchPrices, isLoading],
  );

  return (
    <PricesContext.Provider value={value}>{children}</PricesContext.Provider>
  );
};
